import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react'

import { cn } from '@/lib/utils'

export type BrandTone = 'default' | 'inverse'

const LUMEN_GRADIENT_CLASS: Record<BrandTone, string> = {
  default: 'bg-[linear-gradient(135deg,#2563EB,#0EA5E9_48%,#10B981)] bg-clip-text text-transparent',
  inverse: 'bg-[linear-gradient(135deg,#FFFFFF,#BDEBFF_48%,#A7F3D0)] bg-clip-text text-transparent',
}

export function KindLumenInline({
  className,
  kindClassName,
  lumenClassName,
  tone = 'default',
}: {
  className?: string
  kindClassName?: string
  lumenClassName?: string
  tone?: BrandTone
}) {
  return (
    <span className={cn('whitespace-nowrap', className)}>
      <span className={kindClassName}>Kind</span>
      <span className={cn(LUMEN_GRADIENT_CLASS[tone], lumenClassName)}>Lumen</span>
    </span>
  )
}

export function renderKindLumenText(text: string, tone: BrandTone = 'default'): ReactNode {
  if (!text.includes('KindLumen')) {
    return text
  }

  const parts = text.split('KindLumen')

  return parts.flatMap((part, index) => {
    const nodes: ReactNode[] = []

    if (part) {
      nodes.push(part)
    }

    if (index < parts.length - 1) {
      nodes.push(<KindLumenInline key={`kindlumen-${index}`} tone={tone} />)
    }

    return nodes
  })
}

export function brandifyNode(node: ReactNode, tone: BrandTone = 'default'): ReactNode {
  return Children.map(node, (child) => {
    if (typeof child === 'string') {
      return renderKindLumenText(child, tone)
    }

    if (isValidElement<{ children?: ReactNode }>(child) && child.props.children) {
      return cloneElement(
        child as ReactElement<{ children?: ReactNode }>,
        undefined,
        brandifyNode(child.props.children, tone),
      )
    }

    return child
  })
}
